import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useStudy } from '../../hooks/useStudy';
import { useParticipants } from '../../hooks/useParticipants';
import { useStudies } from '../../hooks/useStudies';
import ParticipantTable from '../../components/studies/ParticipantTable';
import StudyAnalytics from '../../components/studies/StudyAnalytics';
import { StudyStatus } from '../../types/study.types';

type TabType = 'overview' | 'participants' | 'analytics' | 'settings';

const StudyDetail: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const { study, loading: studyLoading, error: studyError, toggleEnrollment, activateStudy, pauseStudy, completeStudy } = useStudy(studyId || '');
  const { participants, loading: participantsLoading, error: participantsError } = useParticipants(studyId || '');
  const { getNextParticipantCode, generateRCTCodes } = useStudies();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showQRModal, setShowQRModal] = useState(false);
  const [nextCode, setNextCode] = useState<string | null>(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loading = studyLoading || participantsLoading;
  const error = studyError || participantsError;

  const enrollmentUrl = study ? `${window.location.origin}/enroll/${study.id}` : '';

  const handleGetNextCode = async () => {
    if (!studyId) return;
    setLoadingCode(true);
    setActionError(null);
    try {
      const code = await getNextParticipantCode(studyId);
      setNextCode(code);
    } catch (err: any) {
      setActionError(err.message || 'Failed to get next code');
    } finally {
      setLoadingCode(false);
    }
  };

  const handleGenerateRCTCodes = async () => {
    if (!studyId) return;
    const count = parseInt(prompt('How many codes to generate?') || '0', 10);
    if (count <= 0) return;

    setLoadingAction(true);
    setActionError(null);
    try {
      await generateRCTCodes(studyId, count);
      alert(`${count} codes generated successfully`);
    } catch (err: any) {
      setActionError(err.message || 'Failed to generate codes');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleEnrollment = async () => {
    setLoadingAction(true);
    setActionError(null);
    try {
      await toggleEnrollment();
    } catch (err: any) {
      setActionError(err.message || 'Failed to toggle enrollment');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'paused' | 'completed') => {
    const confirmMessages: Record<string, string> = {
      active: 'Activate this study? Participants will be able to enroll.',
      paused: 'Pause this study? Enrollment will be temporarily suspended.',
      completed: 'Mark this study as completed? This action cannot be undone.',
    };

    if (!window.confirm(confirmMessages[newStatus])) return;

    setLoadingAction(true);
    setActionError(null);
    try {
      if (newStatus === 'active') await activateStudy();
      else if (newStatus === 'paused') await pauseStudy();
      else if (newStatus === 'completed') await completeStudy();
    } catch (err: any) {
      setActionError(err.message || 'Failed to update study status');
    } finally {
      setLoadingAction(false);
    }
  };

  const getStatusBadgeColor = (status: StudyStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'archived':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-3 text-sm text-gray-600">Loading study...</p>
        </div>
      </div>
    );
  }

  if (error || !study) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error || 'Study not found'}
        </div>
        <button
          onClick={() => navigate('/studies')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          ← Back to Studies
        </button>
      </div>
    );
  }

  const enrollmentStats = {
    enrolled: study.analytics?.totalEnrolled || 0,
    target: study.enrollmentSettings?.targetSampleSize || 0,
    percentage: (study.enrollmentSettings?.targetSampleSize || 0) > 0
      ? Math.round(((study.analytics?.totalEnrolled || 0) / (study.enrollmentSettings?.targetSampleSize || 1)) * 100)
      : 0,
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/studies')}
          className="text-blue-600 hover:text-blue-800 text-sm mb-2"
        >
          ← Back to Studies
        </button>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{study.title}</h1>
            <p className="text-sm text-gray-600">{study.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(study.status)}`}>
              {study.status.charAt(0).toUpperCase() + study.status.slice(1)}
            </span>
            <span className={`px-3 py-1 text-sm font-semibold rounded-full ${study.type === 'rct' ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800'}`}>
              {study.type === 'rct' ? 'RCT' : 'Observational'}
            </span>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {actionError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {actionError}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['overview', 'participants', 'analytics', 'settings'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'participants' && ` (${participants.length})`}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Enrollment</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {enrollmentStats.enrolled} / {enrollmentStats.target}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-teal-600 h-2 rounded-full"
                  style={{ width: `${Math.min(enrollmentStats.percentage, 100)}%` }}
                ></div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Timepoints</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{study.surveySchedule?.length || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Code Prefix</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{study.codeSettings?.prefix}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Created</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatDate(study.createdAt)}</p>
            </div>
          </div>

          {/* Enrollment Actions */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enrollment</h3>
            <div className="flex flex-wrap gap-4">
              {/* QR Code */}
              <div className="flex-1 min-w-[200px]">
                <button
                  onClick={() => setShowQRModal(true)}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-center">
                    <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    <span className="mt-2 block text-sm font-medium text-gray-700">View QR Code</span>
                    <span className="text-xs text-gray-500">For participant enrollment</span>
                  </div>
                </button>
              </div>

              {/* Get Next Code (Observational) */}
              {study.type === 'observational' && (
                <div className="flex-1 min-w-[200px]">
                  <button
                    onClick={handleGetNextCode}
                    disabled={loadingCode || !study.enrollmentSettings?.isOpen}
                    className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {loadingCode ? 'Generating...' : 'Get Next Participant Code'}
                  </button>
                  {nextCode && (
                    <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg text-center">
                      <p className="text-xs text-teal-600 uppercase font-semibold">Participant Code</p>
                      <p className="text-2xl font-mono font-bold text-teal-800">{nextCode}</p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(nextCode);
                          alert('Code copied to clipboard!');
                        }}
                        className="mt-2 text-xs text-teal-600 hover:text-teal-800"
                      >
                        Copy to clipboard
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Generate RCT Codes */}
              {study.type === 'rct' && (
                <div className="flex-1 min-w-[200px]">
                  <button
                    onClick={handleGenerateRCTCodes}
                    disabled={loadingAction}
                    className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
                  >
                    {loadingAction ? 'Generating...' : 'Generate RCT Codes'}
                  </button>
                </div>
              )}
            </div>

            {/* Enrollment Toggle */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Enrollment Status</p>
                  <p className="text-xs text-gray-500">
                    {study.enrollmentSettings?.isOpen ? 'Accepting new participants' : 'Not accepting new participants'}
                  </p>
                </div>
                <button
                  onClick={handleToggleEnrollment}
                  disabled={loadingAction || study.status !== 'active'}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    study.enrollmentSettings?.isOpen
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  } disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed`}
                >
                  {study.enrollmentSettings?.isOpen ? 'Close Enrollment' : 'Open Enrollment'}
                </button>
              </div>
            </div>
          </div>

          {/* Study Status Actions */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Study Status</h3>
            <div className="flex flex-wrap gap-3">
              {study.status === 'draft' && (
                <button
                  onClick={() => handleStatusChange('active')}
                  disabled={loadingAction}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                >
                  Activate Study
                </button>
              )}
              {study.status === 'active' && (
                <button
                  onClick={() => handleStatusChange('paused')}
                  disabled={loadingAction}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-400 transition-colors"
                >
                  Pause Study
                </button>
              )}
              {study.status === 'paused' && (
                <button
                  onClick={() => handleStatusChange('active')}
                  disabled={loadingAction}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                >
                  Resume Study
                </button>
              )}
              {(study.status === 'active' || study.status === 'paused') && (
                <button
                  onClick={() => handleStatusChange('completed')}
                  disabled={loadingAction}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                >
                  Mark as Completed
                </button>
              )}
            </div>
          </div>

          {/* Timepoints Overview */}
          {study.surveySchedule && study.surveySchedule.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Survey Schedule</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Timepoint</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Day</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Survey</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Window</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {study.surveySchedule.map((tp) => (
                      <tr key={tp.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{tp.name}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-600">
                          Day {tp.daysFromEnrollment}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-600">
                          {tp.surveyId || 'Not assigned'}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-500">
                          {tp.windowDaysBefore}-{tp.windowDaysAfter} days
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'participants' && (
        <ParticipantTable
          studyId={studyId || ''}
          participants={participants}
          loading={participantsLoading}
        />
      )}

      {activeTab === 'analytics' && (
        <StudyAnalytics study={study} participants={participants} />
      )}

      {activeTab === 'settings' && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Study Settings</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Study Title</label>
                <p className="mt-1 text-sm text-gray-900">{study.title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Code Prefix</label>
                <p className="mt-1 text-sm text-gray-900">{study.codeSettings?.prefix}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Study Type</label>
                <p className="mt-1 text-sm text-gray-900">{study.type === 'rct' ? 'Randomized Controlled Trial' : 'Observational'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Target Enrollment</label>
                <p className="mt-1 text-sm text-gray-900">{study.enrollmentSettings?.targetSampleSize || 0}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Enrollment Status</label>
                <p className="mt-1 text-sm text-gray-900">{study.enrollmentSettings?.isOpen ? 'Open' : 'Closed'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Protocol ID</label>
                <p className="mt-1 text-sm text-gray-900">{study.protocolId || 'Not set'}</p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => navigate(`/studies/${studyId}/edit`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit Study Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Enrollment QR Code</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 border border-gray-200 rounded-lg">
                <QRCodeSVG value={enrollmentUrl} size={200} />
              </div>
              <p className="mt-4 text-sm text-gray-600 text-center">
                Scan this QR code to access the enrollment page
              </p>
              <p className="mt-2 text-xs text-gray-400 break-all text-center">{enrollmentUrl}</p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(enrollmentUrl);
                    alert('URL copied to clipboard!');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Copy URL
                </button>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyDetail;
